import React from 'react';

import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import CreatableSelect from 'react-select/creatable';

import {
  lang,
  remCss,
  remFontCss,
  rgba,
  scssVariables as variables,
  scssVariables
} from 'src/helpers';
import SVGIcon, { BASE_ICONS } from '../svg';
import LANG_CONST from 'src/helpers/lang/const';
import { MCError } from './MCText';

const animatedComponents = makeAnimated();

class SelectField extends React.PureComponent {
  selectRef;
  constructor(props) {
    super(props);
    this.selectRef = React.createRef();
    this.state = {
      query: '',
      loading: false
    };
  }
  componentDidMount() {
    if (this.selectRef.current && this.props.isMultipleSpecial) {
      this.selectRef.current.inputRef.placeholder =
        (lang.t(this.props.placeholder) || lang.t('Select')) + '...';
    }
  }

  scrollToSelect = () => {
    setTimeout(() => {
      const selected = this.selectRef.current?.menuListRef?.getElementsByClassName(
        'select__option--is-selected'
      );

      if (selected?.length)
        selected[0]?.scrollIntoView({
          behavior: 'instant',
          block: 'center'
        });
    }, 100);
  };

  onMenuOpen = () => {
    this.scrollToSelect();
  };

  render() {
    let {
      name,
      options,
      label,
      description = null,
      errorName = null,
      requiredErrorMessage = null,
      placeholder,
      template = 0,
      value,
      isMultiple,
      isMultipleSpecial = false,
      isOptional = false,
      disabled,
      onChange,
      isClearable = true,
      width = '100%',
      minWidth = undefined,
      invalid = {},
      isSearchable = true,
      allowNew = false,
      ...other
    } = this.props;

    if (isMultipleSpecial) isMultiple = isMultipleSpecial;

    const placeholderTxt = (lang.t(placeholder) || lang.t('Select')) + '...';

    const styleDefault = {
      dropdownIndicator: (provided) => ({
        ...provided,
        padding: remCss(8),
        svg: {
          width: remCss(20),
          height: remCss(20)
        }
      }),
      clearIndicator: (provided) => ({
        ...provided,
        padding: remCss(8),
        svg: {
          width: remCss(20),
          height: remCss(20)
        }
      }),
      valueContainer: (s) => ({
        ...s,
        padding: `${remCss(2)}px ${remCss(8)}px`
      })
    };

    const styles = {
      0: {
        container: (s) => ({ ...s, width: width, minWidth }),
        control: (s) => ({
          ...s,
          ':hover': {
            border: 'none'
          },
          border: 'none',
          paddingTop: remCss(4),
          paddingBottom: remCss(4),
          paddingLeft: remCss(4),
          paddingRight: remCss(4)
        }),
        option: (s, { isSelected }) => ({
          ...s,
          fontSize: remFontCss(18),
          fontFamily: variables['fonts-mc-regular'],
          color: scssVariables['colors-mc-text'],
          backgroundColor: isSelected
            ? rgba(scssVariables['colors-mc-primary'], 0.5)
            : scssVariables['colors-mc-white'],
          ':hover': {
            backgroundColor: rgba(scssVariables['colors-mc-primary'], 0.5)
          }
        }),
        singleValue: (s) => ({
          ...s,
          fontSize: remFontCss(18),
          fontFamily: variables['fonts-mc-regular'],
          color: scssVariables['colors-mc-text']
        }),
        placeholder: (s) => ({
          ...s,
          fontSize: remFontCss(18),
          fontFamily: variables['fonts-mc-regular'],
          color: scssVariables['colors-mc-text-light']
        }),
        ...styleDefault
      },
      1: {
        container: (s) => ({ ...s, width: width }),
        control: (s) => ({
          ...s,
          ':hover': {
            border: 'none'
          },
          border: 'none',
          padding: 0
        }),
        ...styleDefault
      },
      2: {
        container: (s) => ({ ...s, width: width }),
        control: (s) => ({
          ...s,
          border: 'none',
          backgroundColor: 'transparent',
          cursor: disabled ? 'not-allowed' : 'default',
          ':hover': {
            border: 'none'
          },
          paddingTop: remCss(8),
          paddingBottom: remCss(8),
          paddingLeft: remCss(4),
          paddingRight: remCss(4)
        }),
        ...styleDefault
      },
      3: {
        container: (s) => ({ ...s, width: width }),
        control: (s) => ({
          ...s,
          boxShadow: 'none',
          border: 'none',
          backgroundColor: 'transparent',
          cursor: disabled ? 'not-allowed' : 'default',
          ':hover': {
            border: 'none'
          },
          paddingTop: remCss(6),
          paddingBottom: remCss(6),
          paddingLeft: remCss(7),
          paddingRight: remCss(7)
        }),
        multiValue: (styles) => ({
          ...styles,
          backgroundColor: 'white'
        }),
        ...styleDefault
      },
      8: {
        container: (s) => ({ ...s, width: width, minWidth }),
        control: (s) => ({
          ...s,
          ':hover': {
            border: 'none'
          },
          border: 'none',
          paddingTop: remCss(4),
          paddingBottom: remCss(4),
          paddingLeft: remCss(4),
          paddingRight: remCss(4)
        }),
        option: (s, { isSelected }) => ({
          ...s,
          fontSize: remFontCss(18),
          fontFamily: variables['fonts-mc-regular'],
          color: scssVariables['colors-mc-heading'],
          backgroundColor: isSelected
            ? rgba(scssVariables['colors-mc-primary'], 0.5)
            : scssVariables['colors-mc-white'],
          ':hover': {
            backgroundColor: rgba(scssVariables['colors-mc-primary'], 0.5)
          }
        }),
        singleValue: (s) => ({
          ...s,
          fontSize: remFontCss(18),
          fontFamily: variables['fonts-mc-regular'],
          color: scssVariables['colors-mc-heading']
        }),
        placeholder: (s) => ({
          ...s,
          fontSize: remFontCss(18),
          fontFamily: variables['fonts-mc-regular'],
          color: scssVariables['colors-mc-text-light']
        }),

        ...styleDefault
      }
    };
    if (!styles[template]) {
      styles[template] = styles[0];
    }
    let defaultValue = null;

    if (isMultiple && value && options) {
      defaultValue = value.map((v) => options?.find((i) => i.value === v));
    } else if (value && value !== '' && options) {
      defaultValue = options?.find((item) => item.value === value);
    }

    let error = invalid && invalid[name];
    if (error?.includes('required')) {
      if (requiredErrorMessage) {
        error = lang.t(requiredErrorMessage);
      } else {
        errorName = errorName || label || name || placeholder || 'Field';
        error = lang.t(LANG_CONST.validation.required, { label: lang.t(errorName) });
      }
    }

    let Comp = Select;
    if (allowNew) Comp = CreatableSelect;
    return (
      <div className={'mc-select-field-group template-' + template} {...other}>
        {label && (
          <label htmlFor={'mc-' + name}>
            {lang.t(label)}{' '}
            {isOptional && <span className={'mc-label-414'}>({lang.t(LANG_CONST.optional)})</span>}
          </label>
        )}
        {isMultipleSpecial ? (
          <Comp
            ref={this.selectRef}
            inputId={'mc-' + name}
            className='basic-single'
            classNamePrefix='select'
            styles={styles[template]}
            options={options}
            placeholder={''}
            onChange={(a) => {
              onChange(name, a && a.map((item) => item.value), a);
            }}
            isClearable={isClearable}
            components={{
              ...animatedComponents,
              MultiValueContainer: () => null
            }}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: scssVariables['colors-mc-white']
              }
            })}
            isMulti={true}
            value={defaultValue}
            closeMenuOnSelect={true}
            menuPlacement={'auto'}
            menuPosition={'fixed'}
            isDisabled={disabled}
            isSearchable={isSearchable}
          />
        ) : (
          <Comp
            ref={this.selectRef}
            inputId={'mc-' + name}
            onMenuOpen={this.onMenuOpen}
            className='basic-single'
            classNamePrefix='select'
            styles={styles[template]}
            options={options}
            placeholder={placeholderTxt}
            onChange={(a) => {
              onChange(name, a && (isMultiple ? a.map((item) => item.value) : a.value), a);
            }}
            isClearable={isClearable}
            components={animatedComponents}
            isMulti={isMultiple}
            value={defaultValue}
            closeMenuOnSelect={!isMultiple}
            menuPlacement={'auto'}
            menuPosition={'fixed'}
            isDisabled={disabled}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: scssVariables['colors-mc-white']
              }
            })}
            isSearchable={isSearchable}
          />
        )}
        {isMultipleSpecial && (
          <div className={'mc-flex mc-wrap'}>
            {(defaultValue || [])
              .filter((opt) => !!opt)
              .map((opt) => (
                <div key={opt.value} className={'mc-multi-option-value'}>
                  <button
                    type={'button'}
                    className={'mc-no-style-btn'}
                    onClick={() => {
                      const a = defaultValue.filter((old) => old.value !== opt.value);
                      onChange(name, a && a.map((item) => item.value), a);
                    }}
                    style={{ height: remCss(20) }}
                  >
                    <SVGIcon
                      iconname={BASE_ICONS.CLOSE}
                      fill={scssVariables['colors-mc-primary']}
                      width={20}
                    />
                  </button>
                  {lang.t(opt.label)}
                </div>
              ))}
          </div>
        )}
        {!!description && <label className='description'>{description}</label>}
        {error && <MCError error={error} label={label || errorName} />}
      </div>
    );
  }
}

export default SelectField;
